// Logic thuần cho màn xem log cổng trạm: khoảng thời gian + đồng bộ trạng thái lên URL.
// Không gọi network, không đọc window.location — mọi hàm nhận input, trả output, test được độc lập.
// Task 7 sẽ ghép các hàm này vào StationPortalLogs.tsx (không sửa ở task này).

export type LogFilters = {
	search: string;
	sourceApp: string;
	eventType: string;
	level: string;
	action: string;
	actorEmail: string;
	partnerId: string;
};

export type RangeKey = "15m" | "1h" | "24h" | "7d" | "30d" | "custom";

export type LogQueryState = {
	range: RangeKey;
	/** Chỉ dùng khi range === "custom"; ngoài ra suy ra từ range */
	fromDate: string;
	toDate: string;
	filters: LogFilters;
};

const EMPTY_FILTERS: LogFilters = {
	search: "",
	sourceApp: "",
	eventType: "",
	level: "",
	action: "",
	actorEmail: "",
	partnerId: "",
};

/** Mở màn là 1 giờ gần nhất: việc chính là tra sự cố vừa xảy ra. */
export const DEFAULT_STATE: LogQueryState = {
	range: "1h",
	fromDate: "",
	toDate: "",
	filters: { ...EMPTY_FILTERS },
};

const RANGE_MINUTES: Record<Exclude<RangeKey, "custom">, number> = {
	"15m": 15,
	"1h": 60,
	"24h": 60 * 24,
	"7d": 60 * 24 * 7,
	// Tra ngược xa nhất 30 ngày — chặn truy vấn quét log quá sâu gây nặng DB.
	"30d": 60 * 24 * 30,
};

/**
 * Quy đổi một range chọn sẵn (15m/1h/24h/7d/30d) thành cặp mốc ISO tuyệt đối.
 * `now` KHÔNG có giá trị mặc định — bắt buộc mọi nơi gọi hàm này phải tự
 * quyết định lấy thời điểm nào (đọc đồng hồ thật lúc render, hay giữ nguyên
 * timestamp cũ khi rebuild lại query). Nếu để mặc định `new Date()`, một lời
 * gọi thiếu tham số sẽ âm thầm đọc đồng hồ thật — chạy thì tưởng đúng, test
 * thì không tất định, và lỗi chỉ lộ ra khi có ca sự cố lúc nửa đêm.
 */
export function resolveRange(range: RangeKey, now: Date): { fromDate: string; toDate: string } {
	if (range === "custom") return { fromDate: "", toDate: "" };
	const minutes = RANGE_MINUTES[range];
	return {
		fromDate: new Date(now.getTime() - minutes * 60 * 1000).toISOString(),
		toDate: now.toISOString(),
	};
}

/** Đếm số ô lọc đang có giá trị — dùng để hiện badge "đang lọc: N" trên UI. */
export function countActiveFilters(filters: LogFilters): number {
	return Object.values(filters).filter((v) => String(v || "").trim() !== "").length;
}

/** Ghi trạng thái ra query string; bỏ qua tham số rỗng để URL gọn, dễ chia sẻ. */
export function stateToSearch(state: LogQueryState): string {
	const params = new URLSearchParams();
	params.set("range", state.range);
	if (state.range === "custom") {
		if (state.fromDate) params.set("fromDate", state.fromDate);
		if (state.toDate) params.set("toDate", state.toDate);
	}
	(Object.keys(state.filters) as Array<keyof LogFilters>).forEach((key) => {
		const value = String(state.filters[key] || "").trim();
		if (value) params.set(key, value);
	});
	return `?${params.toString()}`;
}

/**
 * Đọc query string ra trạng thái. Bao gồm `partnerId` — lối vào "Xem log của
 * station này" bên Admin truyền partnerId qua URL, đọc sai chỗ này là hỏng
 * tính năng đang chạy trên bản thật.
 */
export function searchToState(search: string): LogQueryState {
	const params = new URLSearchParams(search || "");
	const rawRange = params.get("range") || DEFAULT_STATE.range;
	const range = (
		["15m", "1h", "24h", "7d", "30d", "custom"].includes(rawRange) ? rawRange : DEFAULT_STATE.range
	) as RangeKey;

	const filters = { ...EMPTY_FILTERS };
	(Object.keys(filters) as Array<keyof LogFilters>).forEach((key) => {
		filters[key] = params.get(key) || "";
	});

	return {
		range,
		fromDate: params.get("fromDate") || "",
		toDate: params.get("toDate") || "",
		filters,
	};
}
